<template>
  <transition name="stm-fade">
    <div
      v-if="visible"
      class="stm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="stm-title"
      @mousedown.self="$emit('close')"
    >
      <div class="stm-panel" @mousedown.stop>
        <button type="button" class="stm-close" :aria-label="'Close'" @click="$emit('close')">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div class="stm-header">
          <span class="stm-header__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="8" width="13" height="10" rx="1" />
              <path d="M16 11h3l3 3v4h-6V11z" />
              <circle cx="7.5" cy="19.5" r="1.5" />
              <circle cx="17.5" cy="19.5" r="1.5" />
            </svg>
          </span>
          <h3 id="stm-title" class="stm-header__title">{{ chart ? chart.title : 'Shipping Rate Chart' }}</h3>
        </div>

        <p v-if="chart" class="stm-intro" v-html="chart.intro"></p>

        <div v-if="loadError" class="stm-error">
          {{ loadError }}
          <button type="button" class="stm-retry" @click="loadChart(true)">Retry</button>
        </div>

        <p v-else-if="isLoading" class="stm-loading">Loading the current rate chart…</p>

        <template v-else-if="chart">
          <div v-if="currentValue" class="stm-current">
            <div class="stm-current__row">
              <span class="stm-current__label">Current customer</span>
              <strong class="stm-current__value">{{ currentValue }}</strong>
            </div>
            <span v-if="activeColumnLabel" class="stm-current__slab">{{ activeColumnLabel }}</span>
          </div>

          <div class="stm-table-wrap">
            <table class="stm-table">
              <thead>
                <tr>
                  <th class="stm-table__weight">Parcel Weight</th>
                  <th
                    v-for="col in chart.columns"
                    :key="col.key"
                    :class="{ 'is-active': col.key === zone, 'is-muted': col.selectable === false }"
                  >
                    {{ col.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in chart.rows" :key="row.label">
                  <td class="stm-table__weight">{{ row.label }}</td>
                  <td
                    v-for="(val, i) in row.values"
                    :key="i"
                    :class="{
                      'is-active': chart.columns[i] && chart.columns[i].key === zone,
                      'is-muted': chart.columns[i] && chart.columns[i].selectable === false
                    }"
                  >
                    {{ row.prefix }}₹{{ val }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <ul class="stm-notes">
            <li v-for="(note, i) in chart.notes" :key="i">
              <span v-if="note.charAt(0) === '*'" class="stm-notes__star">*</span>{{ note.charAt(0) === '*' ? note.slice(1) : note }}
            </li>
          </ul>
        </template>

        <div class="stm-actions">
          <button type="button" class="stm-btn" @click="$emit('close')">Got it</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'

// The chart is served by GET /api/shipping/india-post-tariff, which renders
// whichever tariff config/shipping.php has in force — the zone chart (Local /
// Within State / Other States) or the legacy km chart. Mirroring the rates here
// is exactly what let the old hardcoded copy drift from the backend, so don't.
//
// One in-flight request is shared across every mounted instance (OrderForm and
// TeleSales both mount one) and the resolved chart is cached for the session.
let chartCache = null
let chartRequest = null

function fetchChart (apiAdress, force) {
  if (force) {
    chartCache = null
    chartRequest = null
  }
  if (chartCache) return Promise.resolve(chartCache)
  if (!chartRequest) {
    chartRequest = axios.get(`${apiAdress}/api/shipping/india-post-tariff`, { timeout: 15000 })
      .then((response) => {
        chartCache = response.data
        return chartCache
      })
      .catch((error) => {
        chartRequest = null
        throw error
      })
  }
  return chartRequest
}

export default {
  name: 'ShippingTariffModal',
  props: {
    visible: { type: Boolean, default: false },
    // Set when the live chart prices on distance; used for the "~123 km" chip.
    distanceKm: { type: Number, default: null },
    // Key of the chart column this customer falls in — 'within_state',
    // 'other_states', 'col2', … Whichever chart is live, the backend reports it
    // under this one key so the highlight needs no mode-specific logic.
    zone: { type: String, default: null },
    // Human label for that column, e.g. "Within State" or "≤ 1,000 km".
    zoneLabel: { type: String, default: null }
  },
  data () {
    return {
      chart: chartCache,
      isLoading: false,
      loadError: ''
    }
  },
  computed: {
    activeColumnLabel () {
      if (this.zoneLabel) return this.zoneLabel
      const col = (this.chart?.columns || []).find((c) => c.key === this.zone)
      return col ? col.label : ''
    },
    // What the "Current customer" chip reads. The distance chart quotes a
    // distance; the zone chart has none, so it falls back to the zone label.
    currentValue () {
      const km = Number(this.distanceKm)
      if (Number.isFinite(km) && km > 0) {
        return km < 10 ? `~${km.toFixed(1)} km` : `~${Math.round(km)} km`
      }
      return this.zoneLabel || ''
    }
  },
  watch: {
    visible (val) {
      if (val) {
        document.addEventListener('keydown', this.handleKeydown)
        this.loadChart()
      } else {
        document.removeEventListener('keydown', this.handleKeydown)
      }
    }
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    loadChart (force = false) {
      if (this.chart && !force) return
      this.isLoading = true
      this.loadError = ''
      fetchChart(this.$apiAdress, force)
        .then((chart) => {
          this.chart = chart
        })
        .catch(() => {
          this.loadError = 'Could not load the rate chart.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    handleKeydown (e) {
      if (e.key === 'Escape') this.$emit('close')
    }
  }
}
</script>

<style scoped>
.stm-overlay {
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

.stm-panel {
  position: relative;
  width: 100%;
  max-width: 720px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.32), 0 4px 12px rgba(15, 23, 42, 0.08);
  padding: 1.4rem 1.4rem 1.15rem;
  animation: stm-pop 0.28s cubic-bezier(0.18, 1.2, 0.4, 1) both;
  max-height: calc(100vh - 2.5rem);
  overflow-y: auto;
}

.stm-close {
  position: absolute;
  top: 0.55rem;
  right: 0.55rem;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.stm-close:hover { background: #f3f4f6; color: #1f2a44; }
.stm-close:focus { outline: none; }
.stm-close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.stm-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.7rem;
  padding-right: 1.8rem;
}

.stm-header__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ecfdf5;
  color: #0e5b46;
  flex: 0 0 auto;
}

.stm-header__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0e5b46;
}

.stm-intro {
  margin: 0 0 0.85rem;
  font-size: 0.85rem;
  color: #4b5563;
  line-height: 1.45;
}

.stm-current {
  margin: 0 0 0.85rem;
  padding: 0.55rem 0.85rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #ecfdf5 0%, #f5fbf8 100%);
  border: 1px solid #c7ebda;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.stm-current__row {
  display: inline-flex;
  align-items: baseline;
  gap: 0.4rem;
  min-width: 0;
}

.stm-current__label {
  font-size: 0.74rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #4f7868;
}

.stm-current__value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0e5b46;
}

.stm-current__slab {
  font-size: 0.74rem;
  font-weight: 600;
  color: #0e5b46;
  background: #ffffff;
  border: 1px solid #c7ebda;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
}

.stm-loading {
  margin: 0 0 0.85rem;
  font-size: 0.82rem;
  color: #6b7280;
}

.stm-error {
  margin: 0 0 0.85rem;
  padding: 0.6rem 0.85rem;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 0.82rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.stm-retry {
  border: 1px solid #fecaca;
  background: #ffffff;
  color: #991b1b;
  font-size: 0.76rem;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
}

.stm-retry:hover { background: #fff5f5; }
.stm-retry:focus { outline: none; }
.stm-retry:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.3);
}

.stm-table-wrap {
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow: auto;
  /* The zone chart runs to 37 weight slabs — cap the height and let the
     header stick so the active column stays identifiable while scrolling. */
  max-height: 46vh;
  margin-bottom: 0.85rem;
}

.stm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.stm-table th,
.stm-table td {
  padding: 0.5rem 0.65rem;
  text-align: center;
  border-bottom: 1px solid #eef1f7;
  border-right: 1px solid #eef1f7;
  color: #1f2a44;
  white-space: nowrap;
}

.stm-table th:last-child,
.stm-table td:last-child { border-right: 0; }

.stm-table tbody tr:last-child td { border-bottom: 0; }

.stm-table thead th {
  background: #fbfcfe;
  font-weight: 700;
  color: #4f5560;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  position: sticky;
  top: 0;
  z-index: 1;
}

.stm-table__weight {
  text-align: left !important;
  font-weight: 600;
  color: #1f2a44;
  background: #fbfcfe;
}

.stm-table thead .is-active,
.stm-table tbody td.is-active {
  background: #ecfdf5;
  color: #0e5b46;
  font-weight: 700;
}

.stm-table thead .is-active {
  box-shadow: inset 0 -2px 0 #16a07a;
}

/* Columns the live tariff can never select (the printed Zone/Metro band —
   metro destinations are billed at the Other States rate). Shown for
   reference, dimmed so nobody quotes off them. */
.stm-table thead .is-muted,
.stm-table tbody td.is-muted {
  color: #9ca3af;
  font-weight: 400;
}

.stm-notes {
  margin: 0 0 1rem;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.stm-notes li {
  font-size: 0.74rem;
  color: #6b7280;
  line-height: 1.4;
}

.stm-notes__star {
  color: #16a07a;
  font-weight: 700;
  margin-right: 0.15rem;
}

.stm-actions {
  display: flex;
  justify-content: flex-end;
}

.stm-btn {
  height: 38px;
  min-width: 110px;
  padding: 0 1.1rem;
  border-radius: 9px;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.01em;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  font-family: inherit;
}

.stm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.3);
  filter: brightness(1.04);
}

.stm-btn:active { transform: translateY(0); filter: brightness(0.96); }
.stm-btn:focus { outline: none; }
.stm-btn:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22), 0 0 0 3px rgba(22, 160, 122, 0.3);
}

.stm-fade-enter-active,
.stm-fade-leave-active { transition: opacity 0.18s ease; }
.stm-fade-enter,
.stm-fade-leave-to { opacity: 0; }

@keyframes stm-pop {
  0%   { opacity: 0; transform: translateY(8px) scale(0.97); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

@media (max-width: 575px) {
  .stm-panel { padding: 1.1rem 1rem 0.95rem; }
  .stm-current { flex-direction: column; align-items: flex-start; }
}
</style>
